import React, {
  useRef, useEffect, useCallback, useState, useMemo
} from 'react';
import { actionCreators } from 'react/api/actions/chat';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Header from './chatHeader';
import TicketActions from '../TicketActions';
import Body from './chatBody';
import Footer from './chatFooter';

function ChatWindow({
  chat,
  messages,
  ticketSupport,
  sendMessage,
  readMessage,
  readTicketMessages,
  toggleChatWindow,
  removeChatWindow,
  loggedUserId
}) {
  const [ticketActionsIsOpen, setTicketActionsIsOpen] = useState(false);
  const chatHistory = useRef(null);
  const chatHistoryBottomScrollRef = useRef(null);
  const textAreaRef = useRef(null);

  const ticketId = useMemo(() => chat.getIn(['ticket', 'ticketId']), [chat.get('ticket')]);

  const notReadMessages = useMemo(() => messages.filter(
    (message) => !message.get('readDate')
    && message.get('origin') === 'external'
    && String(message.get('type'))?.toLowerCase() === 'text'
    && !message.has('isFromOtherAttendance')
    && !message.get('needTranslation')
  ), [messages]);

  function handleSendMessage() {
    const text = textAreaRef.current.value.trim();

    if (text.length) {
      const to = chat.get('from');

      const parsedTo = ticketId ? to : [to];
      textAreaRef.current.value = '';

      sendMessage({ message: text, to: parsedTo, ticketId });
    }
  }

  const toggleReadMessage = useCallback(() => {
    if (notReadMessages?.size && !ticketId) {
      const arrayOfPromises = notReadMessages
        .map(async (item) => readMessage({
          chatId: chat.get('id'),
          messageId: item.get('id'),
          isTicket: !!ticketId
        }));

      Promise.all(arrayOfPromises);
    } else if (notReadMessages?.size) {
      readTicketMessages({
        chatId: chat.get('id'),
        messages: notReadMessages
      });
    }
  }, [notReadMessages]);

  useEffect(() => {
    chatHistory.current?.addEventListener('click', toggleReadMessage);
    textAreaRef.current?.addEventListener('click', toggleReadMessage);

    return () => {
      chatHistory.current?.removeEventListener('click', toggleReadMessage);
      textAreaRef.current?.removeEventListener('click', toggleReadMessage);
    };
  }, [chat]);

  useEffect(() => {
    if (!chat.get('closed')) {
      chatHistoryBottomScrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className={`chatWindow ${chat.get('closed') && 'closed'}`} id={chat.get('id')}>
      <Header
        chat={chat}
        toggleChatWindow={toggleChatWindow}
        removeChatWindow={removeChatWindow}
      />
      {ticketSupport?.enabled && chat.get('ticket') && (
        <TicketActions
          ticket={chat.get('ticket')}
          toggleChatWindow={() => toggleChatWindow({ chatId: chat.get('id') })}
          setTicketActionsIsOpen={setTicketActionsIsOpen}
        />
      )}
      <Body
        ticketActionsIsOpen={ticketActionsIsOpen}
        chat={chat}
        chatHistory={chatHistory}
        chatHistoryBottomScrollRef={chatHistoryBottomScrollRef}
        messages={messages}
        loggedUserId={loggedUserId}
      />
      <Footer
        ticket={chat.get('ticket')}
        textAreaRef={textAreaRef}
        handleSendMessage={handleSendMessage}
      />
    </div>
  );
}

ChatWindow.propTypes = {
  chat: PropTypes.object.isRequired,
  messages: PropTypes.object.isRequired,
  ticketSupport: PropTypes.object.isRequired,
  sendMessage: PropTypes.func.isRequired,
  readMessage: PropTypes.func.isRequired,
  readTicketMessages: PropTypes.func.isRequired,
  toggleChatWindow: PropTypes.func.isRequired,
  removeChatWindow: PropTypes.func.isRequired,
  loggedUserId: PropTypes.number.isRequired
};

const mapStateToProps = ({ chat, features, user }, { chatId }) => ({
  chat: chat.getIn([`${chatId}`], {}),
  messages: chat.getIn([`${chatId}`, 'messages'], {}),
  ticketSupport: features.get('ENABLE_TICKET_SUPPORT', {}),
  loggedUserId: user.get('id', {}),
});

const mapDispatchToProps = {
  sendMessage: actionCreators.sendMessage,
  readMessage: actionCreators.readMessage,
  toggleChatWindow: actionCreators.toggleChatWindow,
  removeChatWindow: actionCreators.removeChatWindow,
  readTicketMessages: actionCreators.readTicketMessages
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatWindow);
